import React, { Fragment, useState, useEffect } from 'react'
import { useTranslation } from '../../../hooks/useTranslation'
import { getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import { Audio } from '~/components/icons/YnsIcon'
import { useFormatTime } from '~/hooks/useFormatTime'

export function RecommendedBlogsCard({ className }) {
  const { t, locale } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [recommendedBlogs, setRecommendedBlogs] = useState([])
  const [listeningBlog, setListeningBlog] = useState(null)
  const formatTime = useFormatTime()

  useEffect(() => {
    setIsLoading(true)
    getApi()
      .get('/recommended_blogs')
      .then((response) => {
        if (response.status == 200) {
          setRecommendedBlogs(response.data)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [setRecommendedBlogs])

  return (
    <Card
      className={className}
      title={t('recommended_blogs_card.title')}
      actions={
        <Button variant="ghost" href="/coach_services/coach_insights" size="sm">
          {t('recommended_blogs_card.view_all')}
        </Button>
      }
    >
      <Stack spacing={3}>
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Spinner show className="h-9 w-9" />
          </div>
        ) : (
          <Stack spacing={4}>
            {recommendedBlogs.map((blog, index) => (
              <Fragment key={`item-${index}`}>
                {index > 0 && <hr key={`divider-${index}`} />}
                <div
                  key={`item-${index}`}
                  className="flex flex-col gap-3 @sm:flex-row @sm:items-center @sm:justify-between"
                >
                  <div className="flex gap-3">
                    <a href={`/coach_services/coach_insights/${blog['slug']}`}>
                      <img
                        src={blog['image']}
                        alt={blog['title']}
                        className="h-12 min-h-12 w-12 min-w-12 rounded-sm object-cover"
                      />
                    </a>
                    <Stack>
                      <Typography className="font-bold">
                        {blog['title']}
                      </Typography>
                      <Typography className="text-sm text-gray-500">
                        {[
                          blog['author'],
                          formatTime(blog['approved_at'], 'short', locale),
                        ].join(' - ')}
                      </Typography>
                    </Stack>
                  </div>
                  <div>
                    <Stack spacing={1}>
                      <Button
                        variant="outline"
                        size="sm"
                        href={`/coach_services/coach_insights/${blog['slug']}`}
                        rel="noopener noreferrer"
                      >
                        {t('recommended_blogs_card.view_action')}
                      </Button>
                      {blog['audio']['url'] && (
                        <Button
                          stackWrap={false}
                          variant={listeningBlog === blog ? 'solid' : 'outline'}
                          color="secondary"
                          size="sm"
                          onClick={() => {
                            setListeningBlog(
                              listeningBlog === blog ? null : blog,
                            )
                          }}
                          startIcon={<Audio className="stroke-current" />}
                        >
                          {t('recommended_blogs_card.listen_action')}
                        </Button>
                      )}
                    </Stack>
                  </div>
                </div>
                {listeningBlog === blog && blog['audio']['url'] && (
                  <audio
                    className="h-9 w-full"
                    controls
                    preload="metadata"
                    autoPlay
                  >
                    <source src={blog['audio']['url']} type="audio/mpeg" />
                    {t('recommended_blogs_card.audio_not_supported')}
                  </audio>
                )}
              </Fragment>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
